import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Backspace, Gear, Phone, PhoneIncoming, PhoneOutgoing, PhoneX } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full h-11 px-3 rounded-md border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900";
const cardCls = "bg-white border border-zinc-200 rounded-xl p-5";
const labelCls = "block text-xs font-semibold text-zinc-500 mb-1";

const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—");
const fmtWhen = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function Centralino() {
  const [calls, setCalls] = useState([]);
  const [settings, setSettings] = useState(null);
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/centralino/calls"), api.get("/centralino/settings")]);
      setCalls(c.data);
      setSettings(s.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const call = async () => {
    if (number.replace(/\D/g, "").length < 5) { toast.error("Inserisci un numero valido"); return; }
    setBusy(true);
    try {
      await api.post("/centralino/call", { to: number });
      toast.success("Chiamata simulata — registrata nel log");
      setNumber("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { numero_virtuale, inoltro_a, orari_inoltro, registra_chiamate, attivo } = settings;
      await api.put("/centralino/settings", { numero_virtuale, inoltro_a, orari_inoltro, registra_chiamate, attivo });
      toast.success("Impostazioni centralino salvate");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const risposte = calls.filter((c) => c.status === "risposta").length;
  const perse = calls.filter((c) => c.status === "persa").length;

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="centralino-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-2 rounded-md hover:bg-zinc-200 text-zinc-500" data-testid="centralino-back">
            <ArrowLeft size={18} weight="bold" />
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Centralino</h1>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-2.5 py-1"
            data-testid="centralino-mock-badge">Demo</span>
        </div>
        <button onClick={() => setShowSettings(!showSettings)} data-testid="centralino-settings-toggle"
          className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-sm font-semibold text-zinc-700 flex items-center gap-2">
          <Gear size={16} weight="bold" /> Impostazioni numero
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800" data-testid="centralino-mock-notice">
        Struttura in anteprima: dati dimostrativi e chiamate simulate. Il servizio reale (numero virtuale, click-to-call
        e log veri) si attiva quando colleghiamo Twilio Voice al tuo account.
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["Chiamate", calls.length, "centralino-stat-totali"], ["Risposte", risposte, "centralino-stat-risposte"], ["Perse", perse, "centralino-stat-perse"]].map(([l, v, tid]) => (
          <div key={tid} className={cardCls} data-testid={tid}>
            <p className="text-xs font-semibold text-zinc-500">{l}</p>
            <p className="font-display text-3xl font-extrabold text-zinc-900 mt-1">{v}</p>
          </div>
        ))}
      </div>

      {showSettings && settings && (
        <div className={cardCls} data-testid="centralino-settings">
          <h2 className="font-display font-bold text-zinc-900">Impostazioni numero</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <label className={labelCls}>Numero virtuale (assegnato da Twilio)</label>
              <input value={settings.numero_virtuale} onChange={(e) => setSettings({ ...settings, numero_virtuale: e.target.value })}
                placeholder="+39 02 0000000" className={inputCls} data-testid="centralino-numero" />
            </div>
            <div>
              <label className={labelCls}>Inoltro chiamate a</label>
              <input value={settings.inoltro_a} onChange={(e) => setSettings({ ...settings, inoltro_a: e.target.value })}
                placeholder="Il tuo cellulare" className={inputCls} data-testid="centralino-inoltro" />
            </div>
            <div>
              <label className={labelCls}>Orari di inoltro</label>
              <input value={settings.orari_inoltro} onChange={(e) => setSettings({ ...settings, orari_inoltro: e.target.value })}
                placeholder="Es. Sempre, oppure 10:00-23:00" className={inputCls} data-testid="centralino-orari" />
            </div>
            <div className="flex items-end gap-5 pb-1">
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <input type="checkbox" checked={settings.registra_chiamate}
                  onChange={(e) => setSettings({ ...settings, registra_chiamate: e.target.checked })}
                  data-testid="centralino-registra" /> Registra chiamate
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <input type="checkbox" checked={settings.attivo}
                  onChange={(e) => setSettings({ ...settings, attivo: e.target.checked })}
                  data-testid="centralino-attivo" /> Centralino attivo
              </label>
            </div>
          </div>
          <button onClick={saveSettings} disabled={busy} data-testid="centralino-settings-save"
            className="mt-4 h-10 px-5 rounded-md bg-zinc-900 text-white text-sm font-bold disabled:opacity-50">
            Salva impostazioni
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 items-start">
        <div className={cardCls} data-testid="centralino-dialer">
          <h2 className="font-display font-bold text-zinc-900 mb-3">Click-to-call</h2>
          <div className="flex gap-2">
            <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="+39 ..."
              className={`${inputCls} font-mono text-lg`} data-testid="centralino-number" />
            <button onClick={() => setNumber((n) => n.slice(0, -1))} className="px-3 rounded-md border border-zinc-300 text-zinc-500"
              data-testid="centralino-backspace" aria-label="Cancella">
              <Backspace size={18} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((k) => (
              <button key={k} onClick={() => setNumber((n) => (n + k).slice(0, 16))} data-testid={`dial-${k}`}
                className="h-12 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-lg font-bold text-zinc-800 transition-colors">
                {k}
              </button>
            ))}
          </div>
          <button onClick={call} disabled={busy} data-testid="centralino-call-btn"
            className="mt-3 w-full h-12 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors disabled:opacity-50">
            <Phone size={17} weight="fill" /> {busy ? "Chiamata..." : "Chiama (simulata)"}
          </button>
        </div>

        <div className={cardCls} data-testid="centralino-log">
          <h2 className="font-display font-bold text-zinc-900 mb-3">Registro chiamate</h2>
          <div className="space-y-1 max-h-[420px] overflow-y-auto">
            {calls.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0"
                data-testid={`call-row-${c.id}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  c.status === "persa" ? "bg-rose-100 text-rose-600" : c.direction === "out" ? "bg-sky-100 text-sky-600" : "bg-emerald-100 text-emerald-600"}`}>
                  {c.status === "persa" ? <PhoneX size={15} weight="bold" />
                    : c.direction === "out" ? <PhoneOutgoing size={15} weight="bold" />
                    : <PhoneIncoming size={15} weight="bold" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900 truncate">{c.customer_name || c.number}</p>
                  <p className="text-[11px] text-zinc-500">{c.number}{c.customer_name ? ` · ${c.customer_name}` : ""}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-mono text-zinc-600">{fmtDur(c.duration_s)}</p>
                  <p className="text-[10px] text-zinc-400">{fmtWhen(c.created_at)}</p>
                </div>
              </div>
            ))}
            {!calls.length && <p className="text-sm text-zinc-400 py-6 text-center">Nessuna chiamata</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
